<template>
  <div v-if="masterCampaign">
    <vx-card no-shadow>
      <vs-row>
        <h2 class="mr-2">HQ Campaign</h2>
        <vs-chip class="mt-1" :color="masterStatusObject(masterCampaign.masterStatus).color">
          <b>{{ masterStatusObject(masterCampaign.masterStatus).text }}</b>
        </vs-chip>
      </vs-row>
      <div class="vx-row mb-6 mt-2">
        <div class="vx-col sm:w-4/5 w-full">
          <h5 class="my-5" v-if="!isEditable">
            <b>Note: Can no longer be edited as campaign has status {{ masterStatusObject(masterCampaign.masterStatus).text }}.</b>
          </h5>
          <p class="mt-2 mb-2">
            Specify which venues to include in the campaign and compose a base email and/or sms template.
            <br />
            When the campaign is ready for distribution, click the above button to start the campaign. The campaign will then be deligated
            to the chosen venues, which can subsequently adjust the email and/or sms as needed.
            <br />
          </p>
        </div>
      </div>

      <h4 class="mb-2">General info</h4>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Title</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input label="" v-model="masterCampaign.title" class="w-full" :disabled="!isEditable" />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Venues</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <venue-selector v-model="masterCampaign.venues" :multiple="true" :deactivated="!isEditable" />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Campaign medium</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-checkbox v-model="masterCampaign.hasEmail" class="w-full mb-2" :disabled="!isEditable">Email</vs-checkbox>
          <vs-checkbox v-model="masterCampaign.hasSms" class="w-full mb-2" :disabled="!isEditable">Sms</vs-checkbox>
          <vs-checkbox v-model="masterCampaign.hasNpPush" class="w-full" :disabled="!isEditable">NightPay Push</vs-checkbox>
        </div>
      </div>

      <h4 class="mt-8 mb-4">Campaign audience</h4>

      <div class="vx-row mb-8">
        <div class="vx-col sm:w-1/5 w-full">
          <!-- <span>Campaign audience</span> -->
        </div>
        <div class="vx-col sm:w-4/5 w-full">
          <guest-query-builder
            title="Email Audience"
            v-if="masterCampaign.hasEmail"
            :guestQuery.sync="masterCampaign.emailGuestQuery"
            :deactivateVenueSelect="true"
            :dontShowConsentsRow="true"
            :deactivateAll="!isEditable"
          />
          <guest-query-builder
            class="mt-4"
            title="Sms Audience"
            v-if="masterCampaign.hasSms"
            :guestQuery.sync="masterCampaign.smsGuestQuery"
            :deactivateVenueSelect="true"
            :dontShowConsentsRow="true"
            :deactivateAll="!isEditable"
          />
          <guest-query-builder
            class="mt-4"
            title="NightPay Push Audience"
            v-if="masterCampaign.hasNpPush"
            :guestQuery.sync="masterCampaign.npPushGuestQuery"
            :deactivateVenueSelect="true"
            :dontShowConsentsRow="true"
            :deactivateAll="!isEditable"
          />
        </div>
      </div>

      <h4 class="mt-8 mb-4">Campaign dates</h4>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Fillout deadline</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span style="font-size: 11px"
            >This date defines a soft deadline for the Venue Managers to fill out this campaign for their venue
          </span>
          <div class="max-w-sm">
            <DatePicker
              :attributes="calendarAttributes"
              :min-date="new Date()"
              is-expanded
              v-model="masterCampaign.approvedByDate"
            ></DatePicker>
          </div>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Venue send out dates</span>
        </div>
        <div class="vx-col sm:w-4/5 max-w-lg">
          <span style="font-size: 11px">The initial dates to broadcast the campaign mediums for the selected venues.</span>
          <vs-table
            v-if="masterCampaign.venues && masterCampaign.venues.length > 0"
            max-items="10"
            pagination
            :data="masterCampaign.venueCampaignDates"
          >
            <template slot="thead">
              <vs-th sort-key="venueName"> Venue </vs-th>
              <vs-th sort-key="sendEmailDate" v-if="masterCampaign.hasEmail">
                Email Date
                <vs-tooltip
                  position="bottom"
                  text="Change the email send date for ALL campaigns"
                  v-if="isEditable"
                  @click.native.stop="changeEmailDatePopupActive = true"
                >
                  <feather-icon class="cursor-pointer ml-4" icon="EditIcon" svgClasses="w-5 h-5" />
                </vs-tooltip>
              </vs-th>
              <vs-th sort-key="sendSmsDate" v-if="masterCampaign.hasSms">
                Sms Date
                <vs-tooltip
                  position="bottom"
                  delay="140ms"
                  text="Change the sms send date for ALL campaigns"
                  v-if="isEditable"
                  @click.native.stop="changeSmsDatePopupActive = true"
                >
                  <feather-icon class="cursor-pointer ml-4" icon="EditIcon" svgClasses="w-5 h-5" />
                </vs-tooltip>
              </vs-th>
              <vs-th sort-key="sendNpPushDate" v-if="masterCampaign.hasNpPush">
                NightPay Push Date
                <vs-tooltip
                  position="bottom"
                  delay="140ms"
                  text="Change the NightPay Push send date for ALL campaigns"
                  v-if="isEditable"
                  @click.native.stop="changeNpPushDatePopupActive = true"
                >
                  <feather-icon class="cursor-pointer ml-4" icon="EditIcon" svgClasses="w-5 h-5" />
                </vs-tooltip>
              </vs-th>
            </template>

            <template slot-scope="{ data }">
              <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                <vs-td :data="data[indextr].venueName">
                  {{ data[indextr].venueName }}
                </vs-td>

                <vs-td :data="data[indextr].sendEmailDate" v-if="masterCampaign.hasEmail">
                  <span v-if="!isEditable">{{ data[indextr].sendEmailDate | formatDate }}</span>
                  <flat-pickr
                    v-else
                    :key="dateKey"
                    :config="datePickerConfig"
                    v-model="data[indextr].sendEmailDate"
                    placeholder="Email send date"
                    class="p-3 s-inputx vs-input--input flex"
                    :class="{
                      'bg-danger text-white': moment(tr.sendEmailDate).isBefore(moment())
                    }"
                  />
                </vs-td>

                <vs-td :data="data[indextr].sendSmsDate" v-if="masterCampaign.hasSms">
                  <span v-if="!isEditable">{{ data[indextr].sendSmsDate | formatDate }}</span>
                  <flat-pickr
                    v-else
                    :key="dateKey"
                    :config="datePickerConfig"
                    v-model="data[indextr].sendSmsDate"
                    placeholder="Sms send date"
                    class="p-3 s-inputx vs-input--input flex"
                    :class="{
                      'bg-danger text-white': moment(tr.sendSmsDate).isBefore(moment())
                    }"
                  />
                </vs-td>
                <vs-td :data="data[indextr].sendNpPushDate" v-if="masterCampaign.hasNpPush">
                  <span v-if="!isEditable">{{ data[indextr].sendNpPushDate | formatDate }}</span>
                  <flat-pickr
                    v-else
                    :key="dateKey"
                    :config="datePickerConfig"
                    v-model="data[indextr].sendNpPushDate"
                    placeholder="NightPay push send date"
                    class="p-3 s-inputx vs-input--input flex"
                    :class="{
                      'bg-danger text-white': moment(tr.sendNpPushDate).isBefore(moment())
                    }"
                  />
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
          <!-- <div>
            <div>Set default dates for all venues:</div>
            <div class="flex flex-row">
              <span v-if="!isEditable">{{ defaultEmailDate | formatDate }}</span>
              <flat-pickr
                v-else
                :config="datePickerConfig"
                v-model="defaultEmailDate"
                placeholder="Default email send date"
                class="p-3 s-inputx vs-input--input flex"
              />

              <div class="w-6"></div>

              <span v-if="!isEditable">{{ defaultEmailDate | formatDate }}</span>
              <flat-pickr
                v-else
                :config="datePickerConfig"
                v-model="defaultEmailDate"
                placeholder="Default sms send date"
                class="p-3 s-inputx vs-input--input flex"
              />
            </div>
          </div> -->
        </div>
      </div>

      <vs-divider />

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Created info</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span
            >Campaign created at {{ masterCampaign.createdAction.timestamp | formatDate }} by
            {{ masterCampaign.createdAction.userDisplayName }}</span
          >
        </div>
      </div>

      <!-- Save & Remove Button -->
      <vs-row vs-justify="flex-end" class="flex flex-wrap items-center" style="margin-top: 32px">
        <vs-button
          id="save-button"
          v-if="isEditable && hasPermissionForActiveVenue('vp.crm.campaigns.edit')"
          @click="$emit('onSave')"
          class="mt-2"
          >Save Changes</vs-button
        >
      </vs-row>
    </vx-card>

    <vs-prompt
      title="Override email date for all campaigns"
      :active.sync="changeEmailDatePopupActive"
      @accept="applyDefaultEmailDate"
      @close="defaultEmailDate = null"
      accept-text="Override email dates"
    >
      <div class="max-w-sm m-auto">
        <DatePicker mode="dateTime" :min-date="new Date()" is24hr :attributes="calendarAttributes" is-expanded v-model="defaultEmailDate" />
      </div>
    </vs-prompt>
    <vs-prompt
      title="Override sms date for all campaigns"
      :active.sync="changeSmsDatePopupActive"
      @accept="applyDefaultSmsDate"
      @close="defaultSmsDate = null"
      accept-text="Override sms dates"
    >
      <div class="max-w-sm m-auto">
        <DatePicker mode="dateTime" :min-date="new Date()" is24hr :attributes="calendarAttributes" is-expanded v-model="defaultSmsDate" />
      </div>
    </vs-prompt>
    <vs-prompt
      title="Override NightPay push date for all campaigns"
      :active.sync="changeNpPushDatePopupActive"
      @accept="applyDefaultNpPushDate"
      @close="defaultNpPushDate = null"
      accept-text="Override NP push dates"
    >
      <div class="max-w-sm m-auto">
        <DatePicker
          mode="dateTime"
          :min-date="new Date()"
          is24hr
          :attributes="calendarAttributes"
          is-expanded
          v-model="defaultNpPushDate"
        />
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { DatePicker } from "v-calendar";
import campaignMixin from "@/views/mixins/campaignMixin";
import VenueSelector from "@/components/VenueSelector.vue";
import GuestQueryBuilder from "@/components/GuestQueryBuilder.vue";
import moment from "moment";

export default {
  props: ["masterCampaign", "isEditable"],
  mixins: [campaignMixin],
  components: {
    flatPickr,
    DatePicker,
    VenueSelector,
    GuestQueryBuilder
  },
  data() {
    console.log("Master campaign", this.masterCampaign.venues);
    const that = this;

    return {
      moment,
      dateKey: 0,
      changeEmailDatePopupActive: false,
      changeSmsDatePopupActive: false,
      changeNpPushDatePopupActive: false,

      defaultEmailDate: null,
      defaultSmsDate: null,
      defaultNpPushDate: null,

      calendarAttributes: [
        {
          key: "today",
          highlight: "gray"
        }
      ],
      datePickerConfig: {
        enableTime: true, // Set to true for time again
        enableSeconds: false,
        noCalendar: false,
        time_24hr: true,
        dateFormat: "Z",
        altFormat: "Y-m-d H:i", // "d-m-Y H:i" for time 2021-11-19T19:00:00Z
        altInput: true,
        onClose(dates, currentdatestring, picker) {
          console.log("Changed", dates, currentdatestring, picker);
          console.log("Datekey is before", that.dateKey);
          that.dateKey++;
          console.log("Datekey is now", that.dateKey);
        }
      }
    };
  },
  watch: {
    campaignVenues: {
      handler(newValue) {
        // Ensure that the venueCampaignDates are up to date
        // first clear the array
        this.masterCampaign.venueCampaignDates = this.masterCampaign.venueCampaignDates.filter(e =>
          newValue.some(venueId => venueId == e.venueId)
        );
        newValue.forEach(venueId => {
          const venueCampaignDates = this.masterCampaign.venueCampaignDates ? this.masterCampaign.venueCampaignDates : [];

          const venueName = this.venues.find(e => e.venueId == venueId).name;

          // Push it if it does not exist
          const venueCampaignDate = venueCampaignDates.find(e => e.venueId == venueId);
          if (!venueCampaignDate) {
            this.masterCampaign.venueCampaignDates.push({
              venueId: venueId,
              venueName: venueName,
              sendEmailDate: null,
              sendSmsate: null,
              sendNpPushDate: null
            });
          }
        });

        // Ensure that the consent venues are up to date
        this.masterCampaign.smsGuestQuery.consentVenues = newValue;
        this.masterCampaign.emailGuestQuery.consentVenues = newValue;
        this.masterCampaign.npPushGuestQuery.consentVenues = newValue;

        console.log("Campaign venues", newValue, this.masterCampaign.emailGuestQuery);
      }
    }
  },
  methods: {
    applyDefaultEmailDate() {
      this.masterCampaign.venueCampaignDates = this.masterCampaign.venueCampaignDates.map(e => ({
        ...e,
        sendEmailDate: this.defaultEmailDate
      }));
      this.defaultEmailDate = null;
    },
    applyDefaultSmsDate() {
      this.masterCampaign.venueCampaignDates = this.masterCampaign.venueCampaignDates.map(e => ({
        ...e,
        sendSmsDate: this.defaultSmsDate
      }));
      this.defaultSmsDate = null;
    },
    applyDefaultNpPushDate() {
      this.masterCampaign.venueCampaignDates = this.masterCampaign.venueCampaignDates.map(e => ({
        ...e,
        sendNpPushDate: this.defaultNpPushDate
      }));
      this.defaultNpPushDate = null;
    }
  },
  computed: {
    ...mapGetters({
      venues: "venues/venues"
    }),
    // Used to watch this nested property
    campaignVenues() {
      return this.masterCampaign.venues;
    }
  }
};
</script>

<style lang="scss">
</style>